import { reportAppError } from "@/lib/analytics";
import { rewriteLoopbackHostForLanDev } from "@/lib/rewriteLoopbackHostForLanDev";

export const GENI_BACKEND_BASE_URL: string = (() => {
  try {
    const env = (import.meta as { env?: { VITE_GENI_BACKEND_URL?: string } }).env;
    if (env?.VITE_GENI_BACKEND_URL?.trim()) {
      return rewriteLoopbackHostForLanDev(env.VITE_GENI_BACKEND_URL.trim());
    }
  } catch {
    // noop
  }
  return "https://api.geni.cards";
})();

const ACCESS_TOKEN_STORAGE_KEY = "geni_access_token";
const REFRESH_TOKEN_STORAGE_KEY = "geni_refresh_token";
const AUTH_EXPIRED_EVENT = "geni:auth-expired";

/**
 * Shape of structured error responses from geni-backend (see geni-backend's
 * AllExceptionsFilter). `error_code` is a machine-readable enum (e.g.
 * `AI_PROVIDER_TIMEOUT`); the legacy `message` / `error` fields are still
 * populated for backwards compatibility.
 */
interface StructuredBackendError {
  error_code?: string;
  error_message?: string;
  request_id?: string;
  retry_after_seconds?: number | null;
  // Legacy / NestJS-default fields, used as fallbacks.
  message?: string | string[];
  error?: string;
  statusCode?: number;
}

export class AuthHttpError extends Error {
  status: number;
  /** Raw API message when available (useful for 400 validation debugging). */
  apiMessage?: string;
  /** Machine-readable code from the new structured error contract — drives PostHog $exception_type. */
  errorCode?: string;
  /** Server-side request id; pairs frontend exceptions with backend logs. */
  requestId?: string;
  /** Hint from the server (rate limit / breaker open) on how long to wait before retrying. */
  retryAfterSeconds?: number | null;

  constructor(
    status: number,
    message: string,
    options: {
      apiMessage?: string;
      errorCode?: string;
      requestId?: string;
      retryAfterSeconds?: number | null;
    } = {},
  ) {
    super(message);
    this.name = "AuthHttpError";
    this.status = status;
    this.apiMessage = options.apiMessage;
    this.errorCode = options.errorCode;
    this.requestId = options.requestId;
    this.retryAfterSeconds = options.retryAfterSeconds;
  }
}

/** Phone/email login routes must not send a stale Bearer (causes flaky 401). */
export function isCredentialExchangePath(path: string): boolean {
  if (path.startsWith("/auth/phone")) return true;
  if (path === "/auth/login" || path === "/auth/register") return true;
  if (path === "/auth/refresh") return true;
  return false;
}

function readAccessToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
}

/** Access token after successful auth (e.g. for analytics transaction_id). */
export function readStoredAccessToken(): string | null {
  return readAccessToken();
}

function readRefreshToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
}

function emitAuthExpired(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
}

export function setAuthTokens(accessToken: string, refreshToken: string): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
  window.localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
}

export function clearAuthTokens(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
}

export function logout(): void {
  clearAuthTokens();
  emitAuthExpired();
}

export function hasAccessToken(): boolean {
  return Boolean(readAccessToken());
}

export function onAuthExpired(callback: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = () => callback();
  window.addEventListener(AUTH_EXPIRED_EVENT, handler);
  return () => {
    window.removeEventListener(AUTH_EXPIRED_EVENT, handler);
  };
}

type RequestOptions = {
  tokenOverride?: string | null;
};

let refreshRequestPromise: Promise<string | null> | null = null;

function buildHeaders(init: RequestInit, path: string, token: string | null): Headers {
  const headers = new Headers(init.headers ?? {});
  if (!headers.has("Content-Type") && init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  const attachToken = token && !isCredentialExchangePath(path);
  if (attachToken) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return headers;
}

async function request<T>(path: string, init: RequestInit, options: RequestOptions = {}): Promise<T> {
  const token =
    options.tokenOverride !== undefined ? options.tokenOverride : isCredentialExchangePath(path) ? null : readAccessToken();
  const headers = buildHeaders(init, path, token);
  const response = await fetch(`${GENI_BACKEND_BASE_URL}${path}`, {
    ...init,
    headers,
  });
  const rawBody = await response.text();
  // Body may be empty (204, etc.) — surface as null instead of forcing an object.
  let payload: unknown = null;
  if (rawBody.length > 0) {
    try {
      payload = JSON.parse(rawBody);
    } catch (parseError) {
      // Do NOT silently fall back to {} here — that lets non-JSON responses
      // (e.g. NestJS sending a bare string as text/html) corrupt downstream
      // consumers that expect a string and instead get an empty object.
      const snippet = rawBody.slice(0, 200);
      // For error responses with a non-JSON body (e.g. Express's default 404
      // "Cannot PATCH /account/password"), still surface the HTTP status as a
      // structured AuthHttpError so callers can map it to a user-friendly
      // message instead of receiving an opaque Error.
      if (!response.ok) {
        throw new AuthHttpError(response.status, snippet || `Request failed: ${response.status}`, {
          apiMessage: snippet || undefined,
          errorCode: undefined,
          requestId: response.headers.get("x-request-id") ?? undefined,
          retryAfterSeconds: null,
        });
      }
      throw new Error(
        `geniFetch: failed to parse JSON response from ${init.method ?? "GET"} ${path} ` +
          `(status=${response.status}, content-type=${response.headers.get("content-type") ?? "unknown"}): ${snippet}`,
      );
    }
  }
  if (!response.ok) {
    const errPayload = payload as StructuredBackendError | null;
    // Prefer the new structured fields; fall back to legacy NestJS shape so
    // older deploys / non-instrumented endpoints still surface a useful message.
    const legacyMessage = Array.isArray(errPayload?.message)
      ? errPayload?.message.join(", ")
      : errPayload?.message;
    const apiMessage =
      errPayload?.error_message ||
      legacyMessage ||
      errPayload?.error ||
      undefined;
    const requestId =
      // Backend emits both snake_case (preferred) and legacy camelCase.
      errPayload?.request_id ||
      (errPayload as { requestId?: string } | null)?.requestId ||
      response.headers.get("x-request-id") ||
      undefined;
    throw new AuthHttpError(
      response.status,
      apiMessage || `Request failed: ${response.status}`,
      {
        apiMessage,
        errorCode: errPayload?.error_code,
        requestId,
        retryAfterSeconds: errPayload?.retry_after_seconds ?? null,
      },
    );
  }
  return payload as T;
}

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = readRefreshToken();
  if (!refreshToken) return null;

  const response = await fetch(`${GENI_BACKEND_BASE_URL}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    return null;
  }

  const payload = (await response.json().catch(() => null)) as { accessToken?: string; refreshToken?: string } | null;
  if (!payload?.accessToken || !payload?.refreshToken) {
    return null;
  }

  setAuthTokens(payload.accessToken, payload.refreshToken);
  return payload.accessToken;
}

async function refreshAccessTokenOnce(): Promise<string | null> {
  if (!refreshRequestPromise) {
    refreshRequestPromise = refreshAccessToken().finally(() => {
      refreshRequestPromise = null;
    });
  }
  return refreshRequestPromise;
}

function geniReportExtra(path: string, init: RequestInit, error: unknown, more?: Record<string, unknown>): Record<string, unknown> {
  const base: Record<string, unknown> = {
    geni_backend_path: path,
    http_method: init.method ?? "GET",
    ...more,
  };
  if (error instanceof AuthHttpError) {
    base.http_status = error.status;
    if (error.apiMessage) {
      base.geni_api_message = error.apiMessage.slice(0, 300);
    }
    // Structured-error fields — drive PostHog $exception_type, allow cross-referencing
    // a frontend exception with backend logs by request_id, and surface retry hints.
    if (error.errorCode) {
      base.geni_error_code = error.errorCode;
    }
    if (error.requestId) {
      base.geni_request_id = error.requestId;
    }
    if (typeof error.retryAfterSeconds === "number") {
      base.geni_retry_after_seconds = error.retryAfterSeconds;
    }
  }
  return base;
}

/**
 * Paths where a 401 is a *business* outcome ("wrong current password"), NOT a
 * stale access-token. We MUST NOT auto-refresh and retry on those, otherwise:
 *  - the user gets logged out when they simply mistype their current password
 *  - the toast on screen disappears together with the Settings DOM
 */
const NO_AUTO_REFRESH_ON_401 = ["/account/password"];

export async function geniFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  try {
    return await request<T>(path, init);
  } catch (error) {
    const businessAuth401 =
      error instanceof AuthHttpError &&
      error.status === 401 &&
      NO_AUTO_REFRESH_ON_401.some((p) => path.startsWith(p));
    if (
      !(error instanceof AuthHttpError) ||
      error.status !== 401 ||
      path.startsWith("/auth/") ||
      businessAuth401
    ) {
      reportAppError({
        channel: "geni_backend",
        error,
        extra: geniReportExtra(path, init, error),
      });
      throw error;
    }

    const nextAccessToken = await refreshAccessTokenOnce();
    if (!nextAccessToken) {
      clearAuthTokens();
      emitAuthExpired();
      reportAppError({
        channel: "geni_backend",
        error,
        extra: geniReportExtra(path, init, error, { geni_auth_note: "refresh_failed_after_401" }),
      });
      throw error;
    }

    try {
      return await request<T>(path, init, { tokenOverride: nextAccessToken });
    } catch (retryError) {
      if (retryError instanceof AuthHttpError && retryError.status === 401) {
        clearAuthTokens();
        emitAuthExpired();
      }
      reportAppError({
        channel: "geni_backend",
        error: retryError,
        extra: geniReportExtra(path, init, retryError, { geni_after_token_refresh: true }),
      });
      throw retryError;
    }
  }
}
